import React from 'react';
import { Box, Container, Typography, Chip, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RadarIcon from '@mui/icons-material/Radar';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BoltIcon from '@mui/icons-material/Bolt';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { Link as ScrollLink } from 'react-scroll';

const CYCLE_ICONS = [
  <VisibilityIcon sx={{ fontSize: '2rem' }} />,
  <RadarIcon sx={{ fontSize: '2rem' }} />,
  <PsychologyIcon sx={{ fontSize: '2rem' }} />,
  <BoltIcon sx={{ fontSize: '2rem' }} />,
  <FactCheckIcon sx={{ fontSize: '2rem' }} />,
];

const renderInlineMarkdown = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Box
          component="span"
          key={idx}
          sx={{
            background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
          }}
        >
          {part.slice(2, -2)}
        </Box>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
};

const AutonomousAgents: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const bullets = r.autonomous.bullets;
  const cycleSteps = r.autonomous.cycleSteps;

  return (
    <Box
      component="section"
      id="autonomous"
      sx={{
        py: { xs: 8, md: 14 },
        backgroundColor: 'rgb(13, 13, 37)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 15% 25%, rgba(0, 245, 255, 0.10) 0%, transparent 45%), radial-gradient(circle at 85% 75%, rgba(255, 0, 255, 0.10) 0%, transparent 45%)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <Chip
            label={t('autonomous.eyebrow')}
            sx={{
              mb: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 700,
              fontSize: '0.78rem',
              letterSpacing: '0.1em',
              border: '1px solid rgba(255, 255, 255, 0.18)',
            }}
          />
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.05fr 1fr' },
            gap: { xs: 5, lg: 7 },
            alignItems: 'center',
            mb: { xs: 8, md: 10 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.6rem', md: '3.4rem' },
                lineHeight: 1.1,
                mb: 3,
                background: 'linear-gradient(45deg, #00F5FF 20%, #FF00FF 80%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('autonomous.heroTitle')}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: { xs: '1rem', md: '1.15rem' },
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              {t('autonomous.heroSubtitle')}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              {bullets.map((bullet, idx) => (
                <motion.div
                  key={bullet.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.3 + idx * 0.1 }}
                >
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.10)',
                      backdropFilter: 'blur(10px)',
                      height: '100%',
                      transition: 'all 0.3s',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                        borderColor: 'rgba(255, 255, 255, 0.22)',
                        transform: 'translateY(-3px)',
                      },
                    }}
                  >
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      {bullet.title}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '0.88rem', lineHeight: 1.5 }}>
                      {bullet.description}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'white',
              }}
            >
              <Box
                component="img"
                src="/images/agent-app-reco-sentinel.png"
                alt={t('autonomous.imageAlt')}
                loading="lazy"
                sx={{ width: '100%', display: 'block' }}
              />
            </Box>
          </motion.div>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <Typography
            sx={{
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.6)',
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              mb: 4,
            }}
          >
            {t('autonomous.cycleTitle')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
              gap: { xs: 2, md: 1 },
              alignItems: 'center',
              mb: 6,
            }}
          >
            {cycleSteps.map((step, idx) => (
              <React.Fragment key={step.title}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: 0.5 + idx * 0.1 }}
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.10)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                        color: 'white',
                      }}
                    >
                      {CYCLE_ICONS[idx]}
                    </Box>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', mb: 0.3 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', lineHeight: 1.4 }}>
                      {step.description}
                    </Typography>
                  </Box>
                </motion.div>
              </React.Fragment>
            ))}
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 4.5 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.04)',
              border: '2px solid transparent',
              backgroundImage: `
                linear-gradient(rgba(13, 13, 37, 0.92), rgba(13, 13, 37, 0.92)),
                linear-gradient(45deg, #00F5FF, #FF00FF)
              `,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              textAlign: 'center',
              maxWidth: 900,
              mx: 'auto',
            }}
          >
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: { xs: '1.05rem', md: '1.25rem' },
                lineHeight: 1.5,
                fontWeight: 500,
              }}
            >
              {renderInlineMarkdown(t('autonomous.callout'))}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <ScrollLink to="demo" smooth duration={500} offset={-70}>
              <Button
                size="large"
                startIcon={<PlayCircleIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 12px 32px rgba(0, 245, 255, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 16px 40px rgba(255, 0, 255, 0.45)',
                  },
                }}
              >
                {t('autonomous.cta')}
              </Button>
            </ScrollLink>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AutonomousAgents;
